import api from './axios';

// ── Impersonation access ─────────────────────────────────────────────────────

export interface TokenExchangeStatus {
  total: number;
  present_count: number;
  missing_count: number;
  unprovisioned_count: number;
  failed_count: number;
  present: string[];
  missing: { company_name: string; company_code: string; admin_url_slug: string }[];
  unprovisioned: string[];
  failed: string[];
}

export interface TokenExchangeRepair {
  total: number;
  fixed_count: number;
  already_count: number;
  failed_count: number;
  fixed: string[];
  already: string[];
  failed: string[];
}

export const getTokenExchangeStatus = (): Promise<{ data: TokenExchangeStatus }> =>
  api.get('/super-admin/zitadel/token-exchange-status');

export const repairTokenExchange = (): Promise<{ data: TokenExchangeRepair }> =>
  api.post('/super-admin/zitadel/repair-token-exchange');

// ── Admin email sync ─────────────────────────────────────────────────────────

export interface AdminEmailStatus {
  total: number;
  ok_count: number;
  fixable_count: number;
  ambiguous_count: number;
  unreachable_count: number;
  fixable: { company_code: string; admin_name: string; old_email: string; new_email: string }[];
  ambiguous: { company_code: string; admin_name: string; old_email: string }[];
  unreachable: { company_code: string }[];
}

export interface AdminEmailRepair {
  total: number;
  fixed_count: number;
  skipped_count: number;
  failed_count: number;
  fixed: string[];
  skipped: string[];
  failed: string[];
}

export const getAdminEmailStatus = (): Promise<{ data: AdminEmailStatus }> =>
  api.get('/super-admin/maintenance/admin-email-status');

export const syncAdminEmails = (): Promise<{ data: AdminEmailRepair }> =>
  api.post('/super-admin/maintenance/sync-admin-emails');

// ── Tenant schema verification ───────────────────────────────────────────────

export interface SchemaStatus {
  total: number;
  in_sync_count: number;
  drifted_count: number;
  unreachable_count: number;
  drifted: {
    company_code: string;
    database_name: string;
    missing_table_count: number;
    missing_column_count: number;
    missingTables: string[];
    missingColumns: string[];
  }[];
  unreachable: string[];
}

export const getSchemaStatus = (): Promise<{ data: SchemaStatus }> =>
  api.get('/super-admin/maintenance/schema-status');

// ── Login redirect URIs ──────────────────────────────────────────────────────

export interface RedirectUriStatus {
  total: number;
  ok_count: number;
  missing_count: number;
  unprovisioned_count: number;
  missing: { company_code: string; admin_url_slug: string; missing: string[] }[];
  unprovisioned: string[];
  failed: string[];
}

export interface RedirectUriRepair {
  total: number;
  updated_count: number;
  unchanged_count: number;
  failed_count: number;
  updated: string[];
  unchanged: string[];
  failed: string[];
}

export const getRedirectUriStatus = (): Promise<{ data: RedirectUriStatus }> =>
  api.get('/super-admin/zitadel/redirect-uri-status');

export const backfillRedirectUris = (): Promise<{ data: RedirectUriRepair }> =>
  api.post('/super-admin/zitadel/backfill-redirect-uris');

// ── Photo storage health ─────────────────────────────────────────────────────

export interface StorageStatus {
  total: number;
  configured_count: number;
  unconfigured_count: number;
  configured: string[];
  unconfigured: { company_code: string; company_name: string }[];
  failed: string[];
}

export const getStorageStatus = (): Promise<{ data: StorageStatus }> =>
  api.get('/super-admin/maintenance/storage-status');

// ── Zitadel org consistency ──────────────────────────────────────────────────

export interface OrgConsistency {
  total: number;
  consistent_count: number;
  org_not_found_count: number;
  no_admin_in_org_count: number;
  unprovisioned_count: number;
  issues: { company_code: string; reason: string; org_id: string }[];
  failed: string[];
}

export const getOrgConsistency = (): Promise<{ data: OrgConsistency }> =>
  api.get('/super-admin/maintenance/org-consistency');
